import { FC, useMemo } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { ComboboxMulti, FreeInputCombobox } from "@/components/elements/Combobox";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { text } from "@/theme/colors";
import { INPUT_BOX_HEIGHT } from "@/constants/ui";
import { TCaseListFilterFormExtended } from "@/features/case/components/CaseList/case-list-filter-shared";
import { useCaseListFilterMasterData } from "@/features/case/components/CaseList/use-case-list-filter-master-data";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";

type CaseListFilterFormFieldsProps = {
  filterValues: TCaseListFilterFormExtended;
  onFilterChange: (field: keyof TCaseListFilterFormExtended, value: unknown) => void;
};

export const CaseListFilterFormFields: FC<CaseListFilterFormFieldsProps> = ({ filterValues, onFilterChange }) => {
  const { truncatedOptions } = useCaseListFilterMasterData();
  const { provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();

  // Municipality options = union of all municipalities across selected provinces
  const unionedMunicipalityOptions = useMemo(() => {
    const provinces = filterValues.workplaceProvinceCode ?? [];
    if (provinces.length === 0) return [];
    const seen = new Set<string>();
    const acc: { label: string; value: string }[] = [];
    for (const province of provinces) {
      const opts = getMunicipalityOptionsByProvince(province);
      for (const opt of opts) {
        const key = String(opt.value);
        if (seen.has(key)) continue;
        seen.add(key);
        acc.push({ label: opt.label, value: key });
      }
    }
    return acc;
  }, [filterValues.workplaceProvinceCode, getMunicipalityOptionsByProvince]);

  // When provinces change, prune municipalities whose province is no longer selected
  const handleProvinceChange = (nextProvinces: string[]) => {
    onFilterChange("workplaceProvinceCode", nextProvinces);
    const validMunicipalityValues = new Set(
      nextProvinces.flatMap((p) => getMunicipalityOptionsByProvince(p).map((o) => String(o.value))),
    );
    const pruned = (filterValues.workplaceMunicipalityCode ?? []).filter((m) => validMunicipalityValues.has(String(m)));
    if (pruned.length !== (filterValues.workplaceMunicipalityCode ?? []).length) {
      onFilterChange("workplaceMunicipalityCode", pruned);
    }
  };

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">ステータス</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.caseStatus}
            onChange={(value) => onFilterChange("caseStatus", value)}
            options={truncatedOptions.caseStatus}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">案件管理番号</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.managementNumber}
            onChange={(value) => onFilterChange("managementNumber", value)}
            options={truncatedOptions.managementNumber}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">案件名</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.caseName}
            onChange={(value) => onFilterChange("caseName", value)}
            options={truncatedOptions.caseName}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">求人名</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.jobName}
            onChange={(value) => onFilterChange("jobName", value)}
            options={truncatedOptions.jobName}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">企業名</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.companyName}
            onChange={(value) => onFilterChange("companyName", value)}
            options={truncatedOptions.companyName}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">分野</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.companySpecificIndustryId}
            onChange={(value) => onFilterChange("companySpecificIndustryId", value)}
            options={truncatedOptions.industry}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">勤務地（都道府県）</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.workplaceProvinceCode}
            onChange={(value) => handleProvinceChange(value as string[])}
            options={provinceOptions}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">勤務地（市区町村）</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.workplaceMunicipalityCode}
            onChange={(value) => onFilterChange("workplaceMunicipalityCode", value)}
            options={unionedMunicipalityOptions}
            disabled={(filterValues.workplaceProvinceCode ?? []).length === 0}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">募集人数</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.recruitmentNumber}
            onChange={(value) => onFilterChange("recruitmentNumber", value)}
            options={truncatedOptions.recruitmentNumber}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">提案人数</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.caseMatchCount}
            onChange={(value) => onFilterChange("caseMatchCount", value)}
            options={truncatedOptions.caseMatchCount}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">案件担当者</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.personInChargeId}
            onChange={(value) => onFilterChange("personInChargeId", value)}
            options={truncatedOptions.personInCharge}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">登録日</Typography>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <InputDatePicker
                placeholder="選択してください"
                value={filterValues.registrationDateFrom}
                onChange={(value) => onFilterChange("registrationDateFrom", value)}
                nonControl
                label="登録日"
                maxDate={filterValues.registrationDateTo || undefined}
              />
            </Box>
            <Typography variant="cap12" sx={{ color: text.secondary, height: INPUT_BOX_HEIGHT, display: "flex", alignItems: "center" }}>
              〜
            </Typography>
            <Box sx={{ flex: 1 }}>
              <InputDatePicker
                placeholder="選択してください"
                value={filterValues.registrationDateTo}
                onChange={(value) => onFilterChange("registrationDateTo", value)}
                nonControl
                label="登録日"
                minDate={filterValues.registrationDateFrom || undefined}
              />
            </Box>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};
